/**
 * GuestManagementPage — organiser view. Route:
 *   /dashboard/invitations/:invitationId/guests
 *
 * Upload a guest CSV (validated client-side before any API call), review the
 * imported guest list, and send digital-pass invitation emails to selected
 * guests. A summary bar shows total / sent / checked-in counts.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  downloadGuestTemplate,
  importGuests,
  listGuests,
  sendInvitations,
  type Guest,
  type GuestStats,
  type ImportGuestsResult,
} from './api';
import {
  validateCsvText,
  MAX_FILE_BYTES,
  type CsvRowError,
  type CsvValidationResult,
} from './csvValidator';

const EMPTY_STATS: GuestStats = { total: 0, sent: 0, checkedIn: 0, notArrived: 0 };

export function GuestManagementPage() {
  const { invitationId = '' } = useParams<{ invitationId: string }>();

  const [guests, setGuests] = useState<Guest[]>([]);
  const [stats, setStats] = useState<GuestStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(
    async (searchValue = search) => {
      setLoading(true);
      setError(null);
      try {
        const res = await listGuests(invitationId, {
          search: searchValue || undefined,
          limit: 200,
        });
        setGuests(res.data);
        setStats(res.stats);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [invitationId, search],
  );

  useEffect(() => {
    void refresh('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => void refresh(search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const allSelected = guests.length > 0 && selected.size === guests.length;
  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === guests.length && guests.length > 0
        ? new Set()
        : new Set(guests.map((g) => g.id))
    );
  }, [guests]);

  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const ids = selected.size > 0 ? Array.from(selected) : undefined;
      const { queued } = await sendInvitations(invitationId, ids);
      showToast(`${queued} invitation${queued === 1 ? '' : 's'} queued`);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      showToast(`Failed: ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 text-foreground bg-surface">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link
            to="/dashboard/invitations"
            className="text-sm text-primary hover:underline"
          >
            ← Invitations
          </Link>
          <h1 className="text-2xl font-bold text-foreground mt-1">Guests</h1>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors cursor-pointer"
        >
          Upload CSV
        </button>
      </div>

      <StatsBar stats={stats} />

      <div className="flex flex-wrap items-center gap-2 my-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          className="border border-border bg-card text-foreground rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <button
          onClick={handleSend}
          disabled={sending || guests.length === 0}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors cursor-pointer dark:bg-emerald-700 dark:hover:bg-emerald-600"
        >
          {sending
            ? 'Sending…'
            : selected.size > 0
              ? `Send to ${selected.size} selected`
              : 'Send to all not-sent'}
        </button>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive text-sm rounded-lg p-3 mb-3 border border-destructive/20">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        </div>
      ) : guests.length === 0 ? (
        <EmptyState onUpload={() => setShowUpload(true)} />
      ) : (
        <GuestTable
          guests={guests}
          selected={selected}
          allSelected={allSelected}
          onToggleAll={toggleAll}
          onToggleOne={toggleOne}
        />
      )}

      {showUpload && (
        <CsvUploadModal
          invitationId={invitationId}
          onClose={() => setShowUpload(false)}
          onImported={(res) => {
            setShowUpload(false);
            showToast(
              `Imported ${res.imported}, skipped ${res.skipped} duplicate${res.skipped === 1 ? '' : 's'}`,
            );
            void refresh();
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-card text-foreground border border-border text-sm px-4 py-2 rounded-lg shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

function StatsBar({ stats }: { stats: GuestStats }) {
  const items = [
    { label: 'Total', value: stats.total, color: 'text-foreground' },
    { label: 'Invited', value: stats.sent, color: 'text-primary dark:text-indigo-400' },
    { label: 'Checked in', value: stats.checkedIn, color: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'Not arrived', value: stats.notArrived, color: 'text-amber-600 dark:text-amber-400' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map((it) => (
        <div key={it.label} className="bg-card border border-border rounded-xl p-4 shadow-xs">
          <div className={`text-2xl font-bold ${it.color}`}>{it.value}</div>
          <div className="text-xs text-muted-foreground mt-1">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

const STATUS_BADGE: Record<string, string> = {
  NOT_SENT: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  SENT: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-450',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
  NOT_CHECKED_IN: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  CHECKED_IN: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
};

function Badge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[status] ?? 'bg-slate-100 text-slate-600'}`}
    >
      {status.replace(/_/g, ' ').toLowerCase()}
    </span>
  );
}

const GuestTableRow = memo(function GuestTableRow({
  guest,
  isSelected,
  onToggle,
}: {
  guest: Guest;
  isSelected: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <tr className="border-t border-border hover:bg-accent/40 text-foreground">
      <td className="p-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(guest.id)}
        />
      </td>
      <td className="p-3 font-medium text-foreground">
        {guest.firstName} {guest.lastName}
      </td>
      <td className="p-3 text-muted-foreground">{guest.email}</td>
      <td className="p-3 text-muted-foreground">{guest.mobile ?? '—'}</td>
      <td className="p-3">
        <Badge status={guest.inviteStatus} />
      </td>
      <td className="p-3 text-muted-foreground">{guest.rsvpStatus.toLowerCase()}</td>
      <td className="p-3">
        <Badge status={guest.checkinStatus} />
      </td>
    </tr>
  );
});

function GuestTable({
  guests,
  selected,
  allSelected,
  onToggleAll,
  onToggleOne,
}: {
  guests: Guest[];
  selected: Set<string>;
  allSelected: boolean;
  onToggleAll: () => void;
  onToggleOne: (id: string) => void;
}) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-x-auto shadow-xs">
      <table className="w-full text-sm">
        <thead className="bg-muted text-muted-foreground text-left">
          <tr>
            <th className="p-3 w-10">
              <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
            </th>
            <th className="p-3">Name</th>
            <th className="p-3">Email</th>
            <th className="p-3">Mobile</th>
            <th className="p-3">Invite</th>
            <th className="p-3">RSVP</th>
            <th className="p-3">Check-in</th>
          </tr>
        </thead>
        <tbody>
          {guests.map((g) => (
            <GuestTableRow
              key={g.id}
              guest={g}
              isSelected={selected.has(g.id)}
              onToggle={onToggleOne}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="bg-card border border-dashed border-border rounded-xl p-10 text-center">
      <div className="text-4xl mb-3">👥</div>
      <h3 className="font-semibold text-foreground">No guests yet</h3>
      <p className="text-muted-foreground text-sm mt-1">
        Upload a CSV to add your guest list.
      </p>
      <button
        onClick={onUpload}
        className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors cursor-pointer"
      >
        Upload CSV
      </button>
    </div>
  );
}

function CsvUploadModal({
  invitationId,
  onClose,
  onImported,
}: {
  invitationId: string;
  onClose: () => void;
  onImported: (result: ImportGuestsResult) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<CsvValidationResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<CsvRowError[]>([]);
  const [importing, setImporting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = async (f: File | null) => {
    setFileError(null);
    setServerErrors([]);
    setValidation(null);
    setFile(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setFileError('File must be a .csv');
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError('File exceeds the 5 MB limit');
      return;
    }
    const text = await f.text();
    setValidation(validateCsvText(text));
    setFile(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setServerErrors([]);
    try {
      const res = await importGuests(invitationId, file);
      if (res.errors.length > 0) {
        setServerErrors(res.errors);
      } else {
        onImported(res);
      }
    } catch (e) {
      setFileError((e as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const clientErrors = validation?.errors ?? [];
  const errors = serverErrors.length > 0 ? serverErrors : clientErrors;
  const canImport =
    !!file && clientErrors.length === 0 && (validation?.valid.length ?? 0) > 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-xs">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-bold text-foreground">Upload guest CSV</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground cursor-pointer">
            ✕
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          <p className="text-sm text-muted-foreground mb-3">
            Columns: <code className="bg-muted px-1.5 py-0.5 rounded text-foreground font-mono text-xs">first_name, last_name, mobile, email</code>.{' '}
            <button
              type="button"
              onClick={() => void downloadGuestTemplate(invitationId)}
              className="text-primary hover:underline font-medium cursor-pointer"
            >
              Download template
            </button>
          </p>

          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void onPick(e.dataTransfer.files?.[0] ?? null);
            }}
            className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
            />
            <p className="text-muted-foreground text-sm">
              {file ? file.name : 'Drag a .csv here, or click to browse'}
            </p>
          </div>

          {fileError && (
            <div className="bg-destructive/10 text-destructive text-sm rounded-lg p-3 mt-3 border border-destructive/20">
              {fileError}
            </div>
          )}

          {validation && (
            <div className="mt-4 text-sm">
              <p className="text-foreground font-medium">
                <span>{validation.valid.length}</span> valid row
                {validation.valid.length === 1 ? '' : 's'}
                {errors.length > 0 && (
                  <span className="text-destructive ml-2">
                    · {errors.length} error{errors.length === 1 ? '' : 's'}
                  </span>
                )}
              </p>

              {errors.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-destructive/20 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-destructive/10 text-destructive text-left">
                      <tr>
                        <th className="p-2">Row</th>
                        <th className="p-2">Field</th>
                        <th className="p-2">Issue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errors.slice(0, 100).map((err, i) => (
                        <tr key={i} className="border-t border-destructive/10 text-foreground">
                          <td className="p-2">{err.row}</td>
                          <td className="p-2 font-medium">{err.field}</td>
                          <td className="p-2 text-muted-foreground">{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {clientErrors.length === 0 && validation.valid.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto border border-border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-muted text-muted-foreground text-left">
                      <tr>
                        <th className="p-2">Name</th>
                        <th className="p-2">Email</th>
                        <th className="p-2">Mobile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.valid.slice(0, 50).map((r, i) => (
                        <tr key={i} className="border-t border-border text-foreground">
                          <td className="p-2">
                            {r.firstName} {r.lastName}
                          </td>
                          <td className="p-2">{r.email}</td>
                          <td className="p-2">{r.mobile ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!canImport || importing}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition-colors cursor-pointer"
          >
            {importing ? 'Importing…' : 'Confirm import'}
          </button>
        </div>
      </div>
    </div>
  );
}
